import {ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {forkJoin, Observable, of, Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import {AlertService, AuthService, HelperService, SubscribersService} from '@coreservices';
import {Constants} from 'src/app/admin-core/constants';
import {NotificationService} from 'src/app/admin-core/apiservices/notification.service';
import {BackendUserService} from 'src/app/admin-core/apiservices/backend-user.service';
import {RequestParamModel} from 'src/app/admin-core/models/request-params.model';
import {applyReaction, NoteStateService} from 'src/app/admin-core/services/note-state/note-state.service';
import {endLoading, repaintView} from 'src/app/admin-core/utils/loading-state.util';
import {
  likeTargetNote,
  notificationBody,
  notificationMessageHtml,
  linkedNote,
  needsRecordName,
  NotificationChip,
  notificationChip,
  notificationSenderName,
  notificationTarget,
  notificationTitle,
  notificationType,
  notificationTypeLabel,
  notificationUserImage,
  NotificationSender,
  rememberedSender,
  rememberSender,
} from 'src/app/admin-core/utils/notification-row.util';

/** Who a notification is from — the All / Team / System tabs. System is a
 *  notification with no sender (`from_user_id` null), which is how every
 *  console-generated note reaches a user. */
export type NotificationSource = 'all' | 'team' | 'system';

/** The main list's length — the latest 20, with the rest on "View all
 *  notifications" — and the step of Recent Completed's "Load more". The PHP
 *  API's own default page size. */
const PAGE_SIZE = 20;

/** The largest page the PHP API serves (`maxPageSize`). A refresh re-reads the
 *  rows already on screen in one request, so it can never ask for more. */
const MAX_REFRESH_SIZE = 500;

/** What this feed passes to `updateUnreadNotification` after its own writes.
 *  The header re-reads the bell count on any value; the feed ignores its own
 *  signal so a write does not refresh the list twice. Kept from the drawer. */
const OWN_SIGNAL = 'isSidebar';

/**
 * The notification list inside the header drawer — its tabs, filters, row
 * actions and paging. (The /notifications page keeps its own original list.)
 *
 * **Views.** The main list (optionally "Only show unread") and Recent
 * Completed, which swaps the list for read notifications, most recently
 * completed first, and shows each row's completed time instead of its received
 * time. A notification's `modified_at` is that time: the API stamps it only
 * when the row is marked read or unread, never when a note edit rewrites it.
 * Across both, the All / Team / System tabs and the sender menu narrow the
 * same server request. Every count is the server's total for the view, never
 * the length of what is loaded.
 *
 * **Paging.** The main list shows the latest 20; "View all notifications" is
 * the way to the rest. A refresh re-reads the top 20 of the view, so when a row
 * is read under "Only show unread" the next one fills in behind it — marking
 * the first 20 read used to leave an empty list while older notifications still
 * existed. Recent Completed keeps "Load more", which asks for the rows after
 * the last one shown (a keyset cursor on the view's own sort), not for page N:
 * rows leave that view as they are unread, and a page number would then skip
 * the rows that moved up into the gap.
 */
@Component({
  standalone: false,
  selector: 'app-notification-feed',
  templateUrl: './notification-feed.component.html',
  styleUrls: ['./notification-feed.component.css'],
})
export class NotificationFeedComponent implements OnInit, OnDestroy {
  /** A row opened its record — the drawer closes itself on this. */
  @Output() navigated = new EventEmitter<void>();
  /** The reader's unread total (all senders), for the host's heading badge. */
  @Output() unreadTotalChange = new EventEmitter<number>();

  readonly constants = Constants;
  readonly sourceTabs: { id: NotificationSource; label: string }[] = [
    {id: 'all', label: 'All'}, {id: 'team', label: 'Team'}, {id: 'system', label: 'System'},
  ];

  source: NotificationSource = 'all';
  /** "Only show unread" — shared by the drawer and the page through the same
   *  key, so the choice survives closing and reopening either (on when unset). */
  isToggledOn = localStorage.getItem('notif_only_unread') !== 'false';
  /** Recent Completed swaps the list for read notifications. */
  showCompleted = false;
  /** The sender menu's pick; null is Everyone. Remembered like the unread
   *  toggle, and shared with the page (see `rememberedSender`). */
  senderId: number | null = null;
  senderOpen = false;
  /** The staff list the @-mention picker uses, with each person's photo. */
  senders: NotificationSender[] = [];
  /** The remembered pick, which names the button until `senders` arrives. */
  private rememberedPick: NotificationSender | null = null;
  /** The sender menu's search box. */
  senderQuery = '';
  /** Your unread notifications per sender id — the menu's numbers. */
  private senderUnread: Record<number, number> = {};
  /** Senders whose photo failed to load; they fall back to initials. */
  readonly failedAvatars = new Set<number>();
  @ViewChild('senderSearch') private senderSearch?: ElementRef<HTMLInputElement>;

  rows: any[] = [];
  counts: Record<NotificationSource, number> = {all: 0, team: 0, system: 0};
  countsLoaded = false;
  unreadTotal = 0;
  /** Rows of the current view the server still holds after the last one shown. */
  remaining = 0;
  /** Blocking veil — first load and every change of view. */
  isLoading = false;
  isLoadingMore = false;

  private userId: any;
  private userProfile: any;
  /** Notifications with a read/unread write on the wire. */
  private readonly readBusy = new Set<any>();
  /** Ticket for the newest list request; older answers are dropped. */
  private seq = 0;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private alertService: AlertService,
    private router: Router,
    private changeDetection: ChangeDetectorRef,
    private subscribersService: SubscribersService,
    public helperFunction: HelperService,
    private notificationService: NotificationService,
    private backendUserService: BackendUserService,
    private authService: AuthService,
    private noteState: NoteStateService,
  ) {
    this.userProfile = JSON.parse(JSON.stringify(this.authService.getAdminUser() ?? {}));
    this.userId = this.userProfile?.id;
    // Reopen on the person picked last time, here or on the page.
    this.rememberedPick = rememberedSender(this.userId);
    this.senderId = this.rememberedPick?.id ?? null;
  }

  /************************************
   * Life cycle hooks start
   ************************************/

  ngOnInit(): void {
    this.reload();
    this.loadSenders();

    // The header's poll (and any screen that posts a mention) says the count
    // moved — re-read the view. Our own signal is skipped: the write that sent
    // it has already refreshed.
    this.subscribersService.updateUnReadNotificationCount.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
      if (value !== OWN_SIGNAL) {
        this.refresh();
      }
    });

    // A like or read/unread change made on a notes card lands on the matching
    // rows here, so the feed never shows a note the page has just liked as
    // unliked, or a row it just read as unread.
    this.noteState.changes$.pipe(takeUntil(this.destroy$)).subscribe((change) => {
      let changed = false;
      this.rows.forEach((notification: any) => {
        if (change.kind === 'read') {
          if (notification?.id == change.notificationId && notification.status != change.status) {
            notification.status = change.status;
            changed = true;
          }
          return;
        }
        const note = this.likeTargetNote(notification);
        if (note?.id == change.noteId) {
          changed = applyReaction(note, change) || changed;
        }
      });
      if (changed) {
        repaintView(this.changeDetection);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /************************************
   * Life cycle hooks end
   ************************************/

  /************************************
   * Fetching start
   ************************************/

  /** A new view: veil, first page, fresh counts. */
  private reload(): void {
    this.isLoading = true;
    this.rows = [];
    this.remaining = 0;
    this.isLoadingMore = false;
    // markForCheck, not detectChanges: this also runs from ngOnInit, inside the
    // first check of this view; every other caller is a click, which repaints.
    this.changeDetection.markForCheck();
    this.fetchTop(PAGE_SIZE);
  }

  /** Re-read the same view after a write or a poll, keeping what is loaded. */
  refresh(): void {
    this.fetchTop(Math.min(Math.max(this.rows.length, PAGE_SIZE), MAX_REFRESH_SIZE));
  }

  /** The top `size` rows of the view, replacing the list, plus its counts. */
  private fetchTop(size: number): void {
    const seq = ++this.seq;
    this.loadCounts(seq);
    if (this.isEmptyView(this.source)) {
      this.rows = [];
      this.remaining = 0;
      this.isLoading = false;
      this.changeDetection.markForCheck();
      return;
    }
    const params = this.listParams(this.source, size);
    this.notificationService.getUserNotifications(params, true).pipe(
      takeUntil(this.destroy$),
      endLoading(this.changeDetection, () => {
        if (seq === this.seq) {
          this.isLoading = false;
        }
      })
    ).subscribe({
      next: (page: any) => {
        if (seq !== this.seq) {
          return;
        }
        this.rows = this.withReactions(page.data || []);
        this.remaining = Math.max(0, (page.totalCount || 0) - this.rows.length);
        this.loadRecordNames(this.rows);
      },
      error: () => { /* reported globally by ErrorInterceptor */ },
    });
  }

  /**
   * The account / contact names the chips of these rows need. The PHP list has
   * none, and expanding the contact there would also send its credentials, so
   * they come from api-v2 (`notification/record-names`, names only) — one
   * request per load, and only when a row is on an account or contact.
   */
  private loadRecordNames(rows: any[]): void {
    const pending = rows.filter((notification: any) => needsRecordName(notification));
    if (!pending.length) {
      return;
    }
    this.notificationService.getNotificationRecordNames(pending.map((notification: any) => notification.id))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (names) => {
          const byId = new Map(names.map((row) => [Number(row.notification_id), row]));
          pending.forEach((notification: any) => {
            const found = byId.get(Number(notification.id));
            const note = linkedNote(notification);
            if (found && note) {
              note.company = found.company;
              note.frontendUser = found.frontendUser;
            }
          });
          repaintView(this.changeDetection);
        },
        error: () => { /* non-blocking — those rows just show no chip */ },
      });
  }

  /** The next page after the last row shown, appended. */
  loadMore(): void {
    const last = this.rows[this.rows.length - 1];
    if (!last || this.isLoadingMore || this.remaining <= 0) {
      return;
    }
    const seq = this.seq;
    this.isLoadingMore = true;
    const params = this.listParams(this.source, PAGE_SIZE);
    params.defaultFilter += this.cursorFilter(last);
    this.notificationService.getUserNotifications(params, true).pipe(
      takeUntil(this.destroy$),
      endLoading(this.changeDetection, () => {
        this.isLoadingMore = false;
      })
    ).subscribe({
      next: (page: any) => {
        // The view changed while this page was in flight.
        if (seq !== this.seq) {
          return;
        }
        const held = new Set(this.rows.map((row: any) => row.id));
        const fresh = this.withReactions((page.data || []).filter((row: any) => !held.has(row.id)));
        this.rows = [...this.rows, ...fresh];
        this.remaining = Math.max(0, (page.totalCount || 0) - (page.data || []).length);
        this.loadRecordNames(fresh);
      },
      error: () => { /* reported globally by ErrorInterceptor */ },
    });
  }

  /**
   * All / Team / System totals for the current view, and the reader's unread
   * total. Team is All minus System, so one request is saved; a sender pick
   * empties System, which has no sender by definition. `seq` is the list
   * request these belong to — counts for a view already left are dropped.
   */
  private loadCounts(seq: number): void {
    this.loadSenderUnread(seq);
    const all$ = this.countOf(this.viewFilter('all'));
    const system$ = this.senderId ? of(0) : this.countOf(this.viewFilter('system'));
    const unreadView = !this.showCompleted && this.isToggledOn && !this.senderId;
    const unread$ = unreadView
      ? of(null)
      : this.countOf(`&filter[to_user_id]=${this.userId}&filter[status]=${Constants.NOTIFICATION_STATUS_INACTIVE}`);

    forkJoin([all$, system$, unread$]).pipe(takeUntil(this.destroy$)).subscribe({
      next: ([all, system, unread]) => {
        if (seq !== this.seq) {
          return;
        }
        this.counts = {all, system, team: Math.max(0, all - system)};
        this.countsLoaded = true;
        this.unreadTotal = unread ?? all;
        this.unreadTotalChange.emit(this.unreadTotal);
        this.subscribersService.updateNotificationBellIcon(this.unreadTotal > 0);
        repaintView(this.changeDetection);
      },
      error: () => { /* reported globally by ErrorInterceptor */ },
    });
  }

  /** The server's total for a filter — one row asked for, the header read. */
  private countOf(filter: string): Observable<number> {
    const params = new RequestParamModel();
    params.pageSize = 1;
    params.fields = ['id'];
    params.defaultFilter = filter;
    return this.notificationService.getUserNotifications(params, true).pipe(
      map((page: any) => page.totalCount || 0)
    );
  }

  /**
   * One request for a tab of the current view. The same filter drives the
   * counts, so a tab's number and its list always describe the same rows.
   */
  private listParams(source: NotificationSource, size: number): RequestParamModel {
    const params = new RequestParamModel();
    // `note.noteReactions` / `noteReaction.note.noteReactions` back the per-row
    // thumb: they say whether the current user has already liked the note. This
    // list is served by the PHP API, whose Note relation is `noteReactions`;
    // api-v2's `note_reaction` is not a name it knows, and Yii drops an unknown
    // expand silently — the thumb then forgot every like on reload, and a second
    // click added another like instead of removing this one.
    params.expand = ['fromUser', 'toUser', 'note.quote', 'note.noteReactions', 'company', 'quoteProduct.quote',
      'noteReaction.note', 'noteReaction.note.quote', 'noteReaction.note.noteReactions'];
    params.pageSize = size;
    params.defaultFilter = this.viewFilter(source);
    // Recent Completed is ordered by when each was completed (modified_at); id
    // breaks ties, which a bulk mark-as-read writes by the hundred.
    params.sorts = this.showCompleted ? '-modified_at,-id' : '-id';
    return params;
  }

  /**
   * The view as `filter[...]` terms: the reader, the read state, the sender
   * and the tab. `NULL` is ActiveDataFilter's null literal, so System is
   * `from_user_id IS NULL` and Team is `NOT (from_user_id IS NULL)`.
   */
  private viewFilter(source: NotificationSource): string {
    let filter = `&filter[to_user_id]=${this.userId}`;
    if (this.showCompleted) {
      filter += `&filter[status]=${Constants.NOTIFICATION_STATUS_ACTIVE}`;
    } else if (this.isToggledOn) {
      filter += `&filter[status]=${Constants.NOTIFICATION_STATUS_INACTIVE}`;
    }
    if (this.senderId) {
      filter += `&filter[from_user_id]=${this.senderId}`;
    } else if (source === 'system') {
      filter += '&filter[from_user_id]=NULL';
    } else if (source === 'team') {
      filter += '&filter[not][from_user_id]=NULL';
    }
    return filter;
  }

  /** A sender pick under System can match nothing — skip the request. */
  private isEmptyView(source: NotificationSource): boolean {
    return !!this.senderId && source === 'system';
  }

  /**
   * Rows strictly after `last` in the view's order. The main list is ordered
   * by id alone. Recent Completed is ordered by modified_at then id, with NULL
   * dates last (MySQL's DESC order), so the cursor is (date, id) and the
   * undated rows come after every dated one.
   */
  private cursorFilter(last: any): string {
    if (!this.showCompleted) {
      return `&filter[id][lt]=${last.id}`;
    }
    if (!last.modified_at) {
      return `&filter[modified_at]=NULL&filter[id][lt]=${last.id}`;
    }
    const at = encodeURIComponent(last.modified_at);
    return `&filter[or][0][modified_at][lt]=${at}`
      + `&filter[or][1][modified_at]=${at}&filter[or][1][id][lt]=${last.id}`
      + '&filter[or][2][modified_at]=NULL';
  }

  /** The @-mention picker's staff list, as the sender menu's options. */
  private loadSenders(): void {
    const params = new RequestParamModel();
    params.fields = ['id', 'first_name', 'last_name', 'profile_picture'];
    params.pageSize = 'all';
    this.backendUserService.getBackEndUser(params, true).pipe(takeUntil(this.destroy$)).subscribe({
      next: (list: any) => {
        this.senders = (list?.data || [])
          .map((user: any) => {
            const name = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
            return {
              id: Number(user.id),
              name,
              picture: (user.profile_picture || '').trim() || null,
              initials: name.split(/\s+/).slice(0, 2).map((word: string) => word.charAt(0)).join('').toUpperCase(),
            };
          })
          .filter((user: any) => user.name)
          .sort((a: any, b: any) => a.name.localeCompare(b.name));
        repaintView(this.changeDetection);
      },
      error: () => { /* non-blocking — the menu just offers Everyone */ },
    });
  }

  /**
   * Your unread notifications counted per sender (api-v2 `GET notification/sender-unread-counts`).
   * Always unread, whatever the tab, toggle or Recent Completed shows, and re-read
   * with every refresh so a number drops as soon as a row is read. `seq` drops an
   * answer that a newer refresh has overtaken.
   */
  private loadSenderUnread(seq: number): void {
    this.notificationService.getSenderUnreadCounts().pipe(takeUntil(this.destroy$)).subscribe({
      next: (rows) => {
        if (seq !== this.seq) {
          return;
        }
        this.senderUnread = Object.fromEntries(rows.map((row) => [Number(row.from_user_id), Number(row.unread)]));
        repaintView(this.changeDetection);
      },
      error: () => { /* non-blocking — the menu just shows no numbers */ },
    });
  }

  /************************************
   * Fetching end
   ************************************/

  /************************************
   * View controls start
   ************************************/

  setSource(source: NotificationSource): void {
    if (this.source === source) {
      return;
    }
    this.source = source;
    this.reload();
  }

  onChangeToggleIcon(): void {
    this.isToggledOn = !this.isToggledOn;
    // Remember the choice so reopening the drawer (or the page) keeps it.
    localStorage.setItem('notif_only_unread', String(this.isToggledOn));
    this.reload();
  }

  setCompleted(on: boolean): void {
    if (this.showCompleted === on) {
      return;
    }
    this.showCompleted = on;
    this.reload();
  }

  toggleSenderMenu(): void {
    this.senderOpen = !this.senderOpen;
    if (this.senderOpen) {
      // Every opening starts with an empty search, ready to type into.
      this.senderQuery = '';
      setTimeout(() => this.senderSearch?.nativeElement.focus());
    }
  }

  closeSenderMenu(): void {
    this.senderOpen = false;
  }

  /** The staff list narrowed by the search box (any part of the name). */
  get filteredSenders() {
    const query = this.senderQuery.trim().toLowerCase();
    return query ? this.senders.filter((person) => person.name.toLowerCase().includes(query)) : this.senders;
  }

  /** How many of your notifications from this person are unread; 0 shows no number. */
  unreadFrom(id: number): number {
    return this.senderUnread[Number(id)] ?? 0;
  }

  onSenderAvatarError(id: number): void {
    this.failedAvatars.add(id);
  }

  pickSender(id: number | null): void {
    this.senderOpen = false;
    if (this.senderId === id) {
      return;
    }
    this.senderId = id;
    this.remember();
    this.reload();
  }

  get senderLabel(): string {
    return this.selectedSender?.name ?? 'Everyone';
  }

  /** The picked person, whose photo leads the button; null is Everyone. */
  get selectedSender(): NotificationSender | null {
    return this.senders.find(person => person.id === this.senderId)
      ?? (this.senderId != null && this.rememberedPick?.id === this.senderId ? this.rememberedPick : null);
  }

  /** Keeps the pick for the next time the panel or the page opens. */
  private remember(): void {
    this.rememberedPick = this.selectedSender;
    rememberSender(this.userId, this.rememberedPick);
  }

  /** A tab other than All, or a picked person, is narrowing the list — the
   *  "Recent Completed" link reads "Clear filters" while it is. */
  get isFiltered(): boolean {
    return this.source !== 'all' || this.senderId != null;
  }

  /** "Clear filters": back to All and Everyone in one reload. The unread toggle
   *  is a view setting, not a filter, so it stays as it is. */
  clearFilters(): void {
    if (!this.isFiltered) {
      return;
    }
    this.source = 'all';
    this.senderId = null;
    this.remember();
    this.senderOpen = false;
    this.reload();
  }

  /** Clicks inside the menu stop propagation, so this is an outside click. */
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.senderOpen) {
      this.senderOpen = false;
      repaintView(this.changeDetection);
    }
  }

  get emptyTitle(): string {
    if (this.showCompleted) {
      return 'Nothing completed yet';
    }
    return this.isToggledOn ? "You're all caught up" : 'No notifications yet';
  }

  get emptySub(): string {
    if (this.senderId || this.source !== 'all') {
      return 'Nothing in this view - try another tab or Everyone.';
    }
    if (this.showCompleted) {
      return 'Notifications you mark as read show up here.';
    }
    return this.isToggledOn ? 'No unread notifications right now.' : 'New activity will show up here.';
  }

  /************************************
   * View controls end
   ************************************/

  /************************************
   * Row actions start
   ************************************/

  /** Per-row ✓ (read) or envelope (unread). */
  setRead(event: Event, notification: any, read: boolean): void {
    event.stopPropagation();
    event.preventDefault();
    this.saveRead(notification, read);
  }

  readSaving(notification: any): boolean {
    return this.readBusy.has(notification?.id);
  }

  /**
   * `PATCH notification/{id}` — then every notes card showing the note follows,
   * the bell re-reads its count, and the view refreshes so a row that left it
   * (read under "Only show unread", unread under Recent Completed) is replaced
   * by the next one.
   */
  private saveRead(notification: any, read: boolean, quiet: boolean = false): void {
    if (!notification?.id || this.readBusy.has(notification.id)) {
      return;
    }
    this.readBusy.add(notification.id);
    const json = {
      status: read ? Constants.NOTIFICATION_STATUS_ACTIVE : Constants.NOTIFICATION_STATUS_INACTIVE,
      type: notification.note_id ? Constants.NOTE_NOTIFICATION : Constants.QUOTE_PRODUCT_NOTIFICATION
    };
    this.notificationService.markNotificationAsRead(json, notification.id).pipe(
      takeUntil(this.destroy$),
      endLoading(this.changeDetection, () => this.readBusy.delete(notification.id))
    ).subscribe({
      next: (response: any) => {
        if (!quiet) {
          this.alertService.success(read
            ? Constants.NOTIFICATION_MARKED_AS_READ_SUCCESS_MESSAGE
            : Constants.NOTIFICATION_MARKED_AS_UNREAD_SUCCESS_MESSAGE);
        }
        notification.status = Number(response?.data?.status ?? json.status);
        const source = notification.note_reaction_id ? notification.noteReaction : notification;
        if (source?.note_id || notification.quote_product_id) {
          const record = source?.note_id ? notification.note : notification.quoteProduct;
          notification['quoteType'] = record?.quote?.type || null;
          this.subscribersService.updateNotificationStatus(notification);
        }
        this.subscribersService.updateUnreadNotification(OWN_SIGNAL);
        this.noteState.publish({kind: 'read', notificationId: notification.id, status: notification.status});
        this.refresh();
      },
      error: () => { /* reported globally by ErrorInterceptor */ },
    });
  }

  /** The note the thumb acts on — see `likeTargetNote` in notification-row.util. */
  private likeTargetNote(notification: any): any {
    return likeTargetNote(notification);
  }

  /** Only rows backed by a note can be liked. */
  canLike(notification: any): boolean {
    return !!this.likeTargetNote(notification)?.id;
  }

  /**
   * Copies the PHP API's `noteReactions` onto `note_reaction`, the name the
   * thumb, the like/unlike handlers and NoteStateService all read and write,
   * so one array backs the row whichever API filled it.
   */
  private withReactions(rows: any[]): any[] {
    rows.forEach((notification: any) => {
      const note = this.likeTargetNote(notification);
      if (note && Array.isArray(note.noteReactions)) {
        note.note_reaction = note.noteReactions;
      }
    });
    return rows;
  }

  /** The current user's own reaction on the linked note, when they've liked it.
   *  Ids are compared as numbers: the API and the stored profile need not agree
   *  on number vs string. */
  private ownReaction(notification: any): any {
    const note = this.likeTargetNote(notification);
    return (note?.note_reaction || []).find((reaction: any) => Number(reaction?.backend_user_id) === Number(this.userId)) || null;
  }

  isLiked(notification: any): boolean {
    return !!this.ownReaction(notification);
  }

  /** Per-row thumb — likes/unlikes the linked note using the same note-reaction
   *  API as the internal notes list. A like also acknowledges the notification,
   *  so an unread row is marked as read in the same interaction (unliking leaves
   *  the read state alone). */
  toggleLike(event: Event, notification: any): void {
    event.stopPropagation();
    event.preventDefault();

    const note = this.likeTargetNote(notification);
    if (!note?.id) {
      return;
    }
    const existingReaction = this.ownReaction(notification);

    if (existingReaction) {
      this.notificationService.deleteNoteReactions(existingReaction.id).pipe(takeUntil(this.destroy$)).subscribe(() => {
        note.note_reaction = (note.note_reaction || []).filter((reaction: any) => reaction?.id !== existingReaction.id);
        repaintView(this.changeDetection);
        // The note's own card (Sample Request, Quote, Account…) follows.
        this.noteState.publishReaction(note.id, this.userProfile, false);
      });
      return;
    }

    this.notificationService.updateNoteReactions({
      note_id: note.id,
      backend_user_id: this.userId
    }).pipe(takeUntil(this.destroy$)).subscribe((response: any) => {
      note.note_reaction = [...(note.note_reaction || []), response?.data];
      repaintView(this.changeDetection);
      this.noteState.publishReaction(note.id, this.userProfile, true, response?.data);
      // Liking is an acknowledgement — the NOTE is read now, not just this row:
      // every other unread notification about the same note goes too, or the
      // note's card would stay unread after the like.
      this.rows
        .filter((other: any) => other !== notification && other?.note_id && other.note_id == note.id && this.isUnread(other))
        .forEach((other: any) => this.saveRead(other, true, true));
      if (this.isUnread(notification)) {
        this.saveRead(notification, true);
      }
    });
  }

  /************************************
   * Row actions end
   ************************************/

  /************************************
   * Navigation start
   ************************************/

  /** Opens the row's record (see `notificationTarget` in notification-row.util).
   *  A note row closes the drawer even when its record has no route. */
  redirectToModule(notification: any): void {
    const target = notificationTarget(notification);
    if (target) {
      this.router.navigate(target.commands, {queryParams: target.queryParams});
    }
    if (target || (notification?.note_id && notification?.note)) {
      this.navigated.emit();
    }
  }

  /************************************
   * Navigation end
   ************************************/

  /************************************
   * Row display start
   ************************************/

  /* The row's photo, type, chip and text read exactly as on the View all
     notifications page — the shared helpers in notification-row.util. */

  getUserImage(profile_picture: any): string {
    return notificationUserImage(profile_picture);
  }

  getNotificationSenderName(fromUser: any): string {
    return notificationSenderName(fromUser);
  }

  notifType(notification: any): string {
    return notificationType(notification);
  }

  typeLabel(type: string): string {
    return notificationTypeLabel(type);
  }

  notifChip(notification: any): NotificationChip | null {
    return notificationChip(notification);
  }

  notifTitle(notification: any): string {
    return notificationTitle(notification);
  }

  /** The message as the row shows it, with payment "Label: value" lines in the
   *  design's box (notification-row.util). Worked out once per row: the template
   *  asks on every change-detection pass, and a refresh brings new row objects. */
  notifMessage(notification: any): string {
    const body = notificationBody(notification);
    const cached = this.messageCache.get(notification);
    if (cached?.body === body) {
      return cached.html;
    }
    const html = notificationMessageHtml(body);
    this.messageCache.set(notification, {body, html});
    return html;
  }

  private readonly messageCache = new WeakMap<object, {body: string; html: string}>();

  isUnread(notification: any): boolean {
    return notification?.status != Constants.NOTIFICATION_STATUS_ACTIVE;
  }

  /************************************
   * Row display end
   ************************************/
}
